import * as React from 'react';
import { toastr } from 'react-redux-toastr';
import { useDataLoader } from 'react-remote-data-hooks';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import api from '../../../../api';
import CreateForm from './CreateForm';
import { useHasPermission } from '../../../../core/hooks';
import {
  AppPermissions,
  convertToUtcConsideringTimezone,
  formatDate,
  Holiday,
} from '../../../../core';
import { StatusCodes } from 'http-status-codes';
const HolidaySettingsTab: React.FunctionComponent = () => {
  const canDeleteHoliday = useHasPermission(AppPermissions.HolidaySettings);
  const canCreateHoliday = useHasPermission(AppPermissions.HolidaySettings);
  const canUpdateHoliday = useHasPermission(AppPermissions.HolidaySettings);
  const [editModal, setEditModal] = useStateWithCallbackLazy<{
    data?: Holiday;
    isOpen?: boolean;
  }>({});
  const { data, reload } = useDataLoader<Holiday[]>(() =>
    api.holiday.loadAllHolidays()
  );
  const [updateError, setUpdateError] = React.useState('');
  const [updating, setUpdating] = React.useState(Boolean);

  const holidayUpdate = async (updateData: Holiday) => {
    setUpdating(true);
    const missingDetails = [];
    if (!updateData.fromDate) {
      missingDetails.push('From Date');
    }
    if (!updateData.toDate) {
      missingDetails.push('To Date');
    }
    if (!updateData.holidayTitle) {
      missingDetails.push('Holiday Title');
    }
    const missingDetailsCount = missingDetails.length;
    if (missingDetailsCount) {
      setUpdateError(
        `${
          missingDetailsCount > 1
            ? missingDetails.slice(0, missingDetailsCount - 1).join(', ')
            : missingDetails.join()
        } ${missingDetailsCount > 1 ? ` And ${missingDetails.pop()}` : ''}  ${
          missingDetailsCount > 1 ? ' Are' : ' Is'
        } Required`
      );
    } else {
      try {
        const id = editModal?.data?._id;
        setUpdating(true);
        const response = id
          ? await api.holiday.updateHoliday(
              {
                ...updateData,
                fromDate: convertToUtcConsideringTimezone(updateData.fromDate!),
                toDate: convertToUtcConsideringTimezone(updateData.toDate!),
              },
              editModal?.data?._id
            )
          : await api.holiday.createHoliday({
              ...updateData,
              fromDate: convertToUtcConsideringTimezone(updateData.fromDate!),
              toDate: convertToUtcConsideringTimezone(updateData.toDate!),
            });

        if (response.status === StatusCodes.OK) {
          setEditModal({}, () => {
            reload();
          });
        } else {
          setUpdateError(response.message || 'Unable to update details');
        }
        setUpdating(false);
      } catch (error) {
        setUpdateError('Unable to update details');
        setUpdating(false);
      }
    }
  };

  const onDelete = async (id?: string) => {
    try {
      await api.holiday.deleteHoliday(id);
      toastr.success('Success', 'Holiday deleted successfully');
      reload();
      closeModal();
    } catch (error) {
      toastr.error('Error', error?.response?.data?.message);
    }
  };

  const closeModal = () => {
    setEditModal({}, () => {
      // console.log("model closed")
    });
  };

  const resetError = () => {
    setUpdateError('');
    setUpdating(false);
  };

  const showEditModal = (modeData: Holiday) => {
    setEditModal({}, () => {
      setEditModal({ data: modeData, isOpen: true }, () => {
        // console.log("model closed")
      });
    });
  };
  const showCreateModal = () => {
    setEditModal({}, () => {
      setEditModal({ isOpen: true }, () => {
        // console.log("model closed")
      });
    });
  };

  return (
    <>
      <div className="col-lg-4 col-md-6 col-12 border-right">
        <div className="d-flex align-items-center py-3">
          <div className="col">
            <h4 className="mb-0 font-weight-semibold">Holidays</h4>
          </div>
          {canCreateHoliday && (
            <div className="col-auto text-right">
              <button
                className="btn btn-link font-normal font-weight-semibold px-2 py-2 d-inline-flex align-items-center"
                onClick={() => showCreateModal()}
              >
                <i className="icon-plus mr-1 font-xs"></i> Add
              </button>
            </div>
          )}
        </div>
        <div className="grid-table grid-table--sm border-top">
          <table>
            <thead>
              <tr>
                <th>
                  <span className="d-inline-flex align-items-center">
                    Title{' '}
                  </span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center">
                    From Date{' '}
                  </span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center">
                    To Date
                  </span>
                </th>
                <th>
                  <span className="d-inline-flex align-items-center">
                    Number Of Days{' '}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.map((item) => {
                return (
                  <tr
                    onClick={() => {
                      if (canDeleteHoliday || canUpdateHoliday) {
                        showEditModal(item);
                      }
                    }}
                    className="cursor-pointer"
                  >
                    <td>{item.holidayTitle || ''}</td>
                    <td> {formatDate(item.fromDate!, 'DD MMM YYYY')}</td>
                    <td> {formatDate(item.toDate!, 'DD MMM YYYY')}</td>
                    <td>{item.numberOfDays || 1}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="col-lg col-md-6 col-12 p-2 setting-detail-block left-0 top-0 bg-white w-100 h-100 zIndex-1">
        {editModal.isOpen && (
          <>
            <div className="text-right d-md-none">
              <i className="icon-close"></i>
            </div>
            <CreateForm
              onSubmit={(formData) => holidayUpdate(formData)}
              isSubmitting={updating}
              error={updateError}
              data={editModal.data}
              onCancel={() => closeModal()}
              onDelete={(id?: string) => onDelete(id)}
              resetError={resetError}
            ></CreateForm>
          </>
        )}
      </div>
    </>
  );
};

export default HolidaySettingsTab;
